import { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { usePortal } from "../../../contexts/PortalContext";
import useDeviceDetect from "../../../hooks/useDeviceDetect";
// Favorite actions are now handled in ReportsMenu component
import ReportsHomePage from "./ReportsHomePage";
import ProfileView from "./ProfileView";
import CustomPageNavigation from "./CustomPageNavigation";
import ZoomControls from "./ZoomControls";
import "./PowerBIPortal.scss";

const PowerBIPortal = () => {
  const dispatch = useDispatch();
  const { setReportMode, setShowMobileStickyHeader, setHandlePageChange, setCurrentActivePage } = usePortal();
  const [reportPages, setReportPages] = useState([]); // All visible pages from the report
  const [currentPage, setCurrentPage] = useState(null);
  const [isPageNavExpanded, setIsPageNavExpanded] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100); // Zoom level in percentage

  // Detect device type for responsive layout
  const { isMobile, isPortrait } = useDeviceDetect();

  // Lock the initial layout type to prevent reload on orientation change
  const [initialLayoutConfig] = useState(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Detect actual mobile devices (not just screen size)
    // Check for touch support and user agent to identify real mobile devices
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(userAgent);

    // Consider it mobile if:
    // 1. It's a mobile device (user agent check), OR
    // 2. It's touch-enabled AND smaller screen (width or height < 768)
    const isInitialMobile = isMobileDevice || (isTouchDevice && (width < 768 || height < 768));
    const isInitialPortrait = height > width;

    const layoutType = isInitialMobile
      ? (isInitialPortrait ? models.LayoutType.MobilePortrait : models.LayoutType.MobileLandscape)
      : models.LayoutType.Master;

    console.log('🔒 Initial layout locked:', {
      width,
      height,
      isMobileDevice,
      isTouchDevice,
      isInitialMobile,
      isInitialPortrait,
      layoutType
    });

    return {
      layoutType,
      isInitialMobile,
      customLayout: isInitialMobile ? { displayOption: models.DisplayOption.FitToWidth } : undefined
    };
  });

  // Log layout detection for testing (but don't change report config)
  useEffect(() => {
    const layoutType = isMobile
      ? (isPortrait ? 'MobilePortrait' : 'MobileLandscape')
      : 'Master (Desktop)';
    console.log('📱 Orientation changed (report will NOT reload):', { isMobile, isPortrait, layoutType });
  }, [isMobile, isPortrait]);

  // Get selected report and embed data from Redux store
  const { selectedReport, embedData, embedLoading, embedError, reports, isProfileView } = useSelector(
    (state) => state.powerBIReports
  );

  // Get favorite reports using is_favorite flag from reports
  const favoriteReports = (reports || []).filter(report => report.is_favorite === true);

  // Get the last viewed report as an array (filtered from reports)
  const lastViewedReport = (reports || []).filter(report => report.is_recently  === true);

  // Switch to report mode when component mounts
  useEffect(() => {
    setReportMode();
  }, [setReportMode]);

  // Reset to fit-to-page and scroll to top when a new report is selected
  useEffect(() => {
    if (selectedReport) {
      // Will be applied when report loads
      console.log('New report selected - will fit to page when loaded');

      // Scroll to top on mobile when a report is selected
      if (isMobile) {
        // Scroll multiple containers to ensure we catch the right one
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Try to scroll all potential containers
        const containers = [
          document.querySelector('.main'),
          document.querySelector('.power-bi-reports-container'),
          document.querySelector('.powerbi-content-container'),
          document.querySelector('.reports-content-area'),
          document.body,
          document.documentElement
        ];

        containers.forEach(container => {
          if (container) {
            container.scrollTop = 0;
          }
        });
      }
    }
  }, [selectedReport?.id, isMobile]); // Only trigger when report ID changes

  // Debug: Log embed data when it changes
  useEffect(() => {
    if (embedData) {
      console.log('Embed Data Received:', {
        embedUrl: embedData.embedUrl,
        reportId: embedData.reportId,
        hasEmbedToken: !!embedData.embedToken,
        hasAccessToken: !!embedData.accessToken,
        customData: embedData.customData,
        expiration: embedData.expiration
      });
    }
  }, [embedData]);

  // Handle page change - accepts page ID (name property from Power BI)
  const handlePageChange = useCallback(async (pageId) => {
    if (!window.report) {
      console.warn('Report not loaded yet');
      return;
    }

    try {
      const pages = await window.report.getPages();
      const targetPage = pages.find(p => p.name === pageId);

      if (targetPage) {
        await targetPage.setActive();
        setCurrentPage(targetPage);
        setCurrentActivePage(targetPage); // Update context so ReportsMenu can highlight the active page
        console.log('Page changed to:', targetPage.displayName, 'ID:', pageId);
      } else {
        console.warn('Page not found with ID:', pageId);
      }
    } catch (error) {
      console.error('Error changing page:', error);
    }
  }, [setCurrentActivePage]); // Add setCurrentActivePage as dependency

  // Register handlePageChange in context so ReportsMenu can use it
  useEffect(() => {
    console.log('Registering handlePageChange in context');
    setHandlePageChange(() => handlePageChange);
    return () => {
      console.log('Unregistering handlePageChange from context');
      setHandlePageChange(null);
    };
  }, [setHandlePageChange, handlePageChange]);

  // Favorite functionality has been moved to ReportsMenu component
  // Export to PDF functionality has been removed

  // Zoom In - Increase zoom by 10% using Power BI native API
  const handleZoomIn = async () => {
    if (!window.report) {
      console.warn('Report not loaded yet');
      return;
    }

    const newZoom = Math.min(zoomLevel + 10, 200); // Max 200%
    const zoomValue = newZoom / 100; // Convert percentage to decimal (0.5 to 2.0)

    try {
      await window.report.setZoom(zoomValue);
      setZoomLevel(newZoom);
      console.log('Zoom IN applied:', `${newZoom}%`);
    } catch (error) {
      console.error('Error setting zoom:', error);
    }
  };

  // Zoom Out - Decrease zoom by 10% using Power BI native API
  const handleZoomOut = async () => {
    if (!window.report) {
      console.warn('Report not loaded yet');
      return;
    }

    const newZoom = Math.max(zoomLevel - 10, 50); // Min 50%
    const zoomValue = newZoom / 100; // Convert percentage to decimal (0.5 to 2.0)

    try {
      await window.report.setZoom(zoomValue);
      setZoomLevel(newZoom);
      console.log('Zoom OUT applied:', `${newZoom}%`);
    } catch (error) {
      console.error('Error setting zoom:', error);
    }
  };

  // Fit to Screen - Update settings to fit to width, then get the calculated zoom
  const handleFitToScreen = async () => {
    if (!window.report) {
      console.warn('Report not loaded yet');
      return;
    }

    try {
      // Update report settings to fit to width
      await window.report.updateSettings({
        customLayout: {
          displayOption: models.DisplayOption.FitToWidth
        }
      });

      // Wait for Power BI to apply the new settings and calculate zoom
      setTimeout(async () => {
        try {
          const currentZoom = await window.report.getZoom();
          const zoomPercentage = Math.round(currentZoom * 100);

          if (zoomPercentage > 0 && zoomPercentage <= 200) {
            setZoomLevel(zoomPercentage);
            console.log('Fit to Screen applied, zoom:', `${zoomPercentage}%`);
          } else {
            setZoomLevel(100);
            console.warn('Invalid zoom after fit, defaulting to 100%');
          }
        } catch (zoomError) {
          console.warn('Could not get zoom after fit:', zoomError);
        }
      }, 300);
    } catch (error) {
      console.error('Error fitting to screen:', error);
    }
  };

  // Handle zoom change from slider
  const handleZoomChange = async (newZoom) => {
    if (!window.report) {
      console.warn('Report not loaded yet');
      return;
    }

    const zoomValue = newZoom / 100; // Convert percentage to decimal

    try {
      await window.report.setZoom(zoomValue);
      setZoomLevel(newZoom);
      console.log('Zoom changed to:', `${newZoom}%`);
    } catch (error) {
      console.error('Error changing zoom:', error);
    }
  };


  // Check if mobile sticky header should be shown
  const showMobileStickyHeader = false; //isMobile && selectedReport?.show_pages && reportPages.length > 0;

  // Update context when sticky header visibility changes
  useEffect(() => {
    setShowMobileStickyHeader(showMobileStickyHeader);
  }, [showMobileStickyHeader, setShowMobileStickyHeader]);

  return (
    <div className={`power-bi-reports-container ${showMobileStickyHeader ? 'has-mobile-sticky-header' : ''}`} style={{ width: '100%', padding: '0' }}>
      <div className="reports-content-area" style={{ padding: '0', width: '100%' }}>
        {isProfileView && (
          <ProfileView />
        )}

        {!isProfileView && !selectedReport && (
          <ReportsHomePage
            favoriteReports={favoriteReports}
            lastViewedReport={lastViewedReport}
          />
        )}

        {!isProfileView && selectedReport && (
          <div style={{ padding: '0', width: '100%', position: 'relative' }}>
            {/* Sticky Header - Mobile Only */}
            {showMobileStickyHeader && (
              <div className="report-sticky-header mobile-only">
              <div className="report-header-content">
                {/* Custom Page Navigation Dropdown for Mobile */}
                  <select
                    className="mobile-page-dropdown"
                    value={currentPage?.name || ''}
                    onChange={(e) => handlePageChange(e.target.value)}
                    style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px',
                      padding: '6px 10px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: '#333',
                      cursor: 'pointer',
                      width: '100%',
                      outline: 'none'
                    }}
                  >
                    {reportPages.map((page) => (
                      <option key={page.name} value={page.name}>
                        {page.displayName}
                      </option>
                    ))}
                  </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {embedLoading && (
                  <div className="loading-indicator">
                    <div className="spinner-border spinner-border-sm me-2" role="status" style={{ borderColor: '#d7b239', borderRightColor: 'transparent' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading...</span>
                  </div>
                )}
              </div>
            </div>
            )}

            {embedLoading && (
              <div className="text-center py-5" style={{ marginTop: '80px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading embed data...</span>
                </div>
                <p className="mt-3 text-muted">Loading report embed URL...</p>
              </div>
            )}

            {embedError && !embedLoading && (
              <div className="alert alert-danger" role="alert" style={{ marginTop: '80px', margin: '80px 1rem 1rem 1rem' }}>
                <i className="fa fa-exclamation-triangle me-2"></i>
                Something went wrong. Please try again. If the problem persists, contact your administrator.
              </div>
            )}

            {!embedLoading && !embedError && embedData && embedData.embedUrl && (embedData.accessToken || embedData.embedToken) && (
              <div className="powerbi-content-container">
                {/* Custom Page Navigation - Desktop Only */}
                {/* {!isMobile && selectedReport?.show_pages && (
                  <CustomPageNavigation
                    pages={organizedPages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onExpandedChange={setIsPageNavExpanded}
                  />
                )} */}

                {/* PowerBI Report Area with Toolbar */}
                <div
                  className="powerbi-report-area"
                  style={{
                    flex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Report Toolbar removed - Zoom controls moved to bottom */}

                  {/* PowerBI Report Container */}
                  <div
                    className="powerbi-embed-wrapper"
                    style={{
                      flex: 1
                    }}
                  >
                  <PowerBIEmbed
                  embedConfig={{
                    type: 'report',
                    id: embedData.reportId || selectedReport.report_id || selectedReport.reportId,
                    embedUrl: embedData.embedUrl,
                    accessToken: embedData.embedToken || embedData.accessToken,
                    tokenType: models.TokenType.Embed,
                    settings: {
                      bars: {
                          actionBar: { visible: true },
                          statusBar: { visible: false } 
                      },
                      panes: {
                        filters: {
                          expanded: false,
                          visible: isMobile ? true : true // Hide filters on mobile to prevent clipping
                        },
                        pageNavigation: {
                          visible: false, // Hide default navigation - use custom component on desktop, custom dropdown on mobile
                          position: models.PageNavigationPosition.Left
                        }
                      },
                      background: models.BackgroundType.Default,
                      // Use locked initial layout type to prevent reload on orientation change
                      // Layout is determined once on initial load and stays fixed
                      layoutType: initialLayoutConfig.layoutType,
                      // Ensure filters and bookmarks persist across page changes
                      filterPaneEnabled: true, // Disable filter pane on mobile
                      navContentPaneEnabled: true,
                      // Custom page size for mobile to prevent content clipping (locked at initial load)
                      customLayout: initialLayoutConfig.customLayout
                    }
                  }}
                  eventHandlers={
                    new Map([
                      ['loaded', async () => {
                        console.log('Report loaded successfully');
                        // Fetch all pages for custom navigation
                        try {
                          const pages = await window.report.getPages();
                          // Filter to show only visible pages (visibility === 0)
                          const visiblePages = pages.filter(page => page.visibility === 0);
                          setReportPages(visiblePages);

                          // Check if there's a target page to navigate to (from menu page click)
                          const targetPageId = sessionStorage.getItem('targetPageId');
                          if (targetPageId) {
                            const targetPage = visiblePages.find(p => p.name === targetPageId);
                            if (targetPage) {
                              await targetPage.setActive();
                              setCurrentPage(targetPage);
                              setCurrentActivePage(targetPage); // Update context
                              console.log('Navigated to target page:', targetPage.displayName);
                            }
                            sessionStorage.removeItem('targetPageId');
                          } else {
                            // Set the first active page as current
                            const activePage = visiblePages.find(p => p.isActive);
                            if (activePage) {
                              setCurrentPage(activePage);
                              setCurrentActivePage(activePage); // Update context
                            }
                          }
                          console.log('Available pages:', visiblePages.map(p => p.displayName));

                          // Get the natural zoom level Power BI uses (don't force fit)
                          setTimeout(async () => {
                            try {
                              const currentZoom = await window.report.getZoom();
                              const zoomPercentage = Math.round(currentZoom * 100);

                              // Only update if we got a valid zoom level
                              if (zoomPercentage > 0 && zoomPercentage <= 200) {
                                setZoomLevel(zoomPercentage);
                                console.log('Report natural zoom level:', `${zoomPercentage}%`);
                              } else {
                                // Default to 100% if zoom is invalid
                                setZoomLevel(100);
                                console.warn('Invalid zoom level, defaulting to 100%');
                              }
                            } catch (zoomError) {
                              console.warn('Could not get zoom level:', zoomError);
                              setZoomLevel(100);
                            }
                          }, 300); // Wait 300ms for Power BI to fully render
                        } catch (err) {
                          console.error('Error fetching pages:', err);
                        }
                      }],
                      ['rendered', () => console.log('Report rendered successfully')],
                      ['pageChanged', (event) => {
                        const newPage = event.detail.newPage;
                        setCurrentPage(newPage);
                        setCurrentActivePage(newPage); // Update context
                        console.log('Page changed to:', newPage.displayName);
                      }],
                      ['error', (event) => {
                        console.error('Report error:', event.detail);
                      }]
                    ])
                  }
                  cssClassName="powerbi-report-container"
                  getEmbeddedComponent={(embeddedReport) => {
                    window.report = embeddedReport;
                  }}
                />
                  </div>
                </div>
              </div>
            )}

            {!embedLoading && !embedError && embedData && (!embedData.embedUrl || (!embedData.accessToken && !embedData.embedToken)) && (
              <div className="alert alert-warning" role="alert" style={{ marginTop: '80px', margin: '80px 1rem 1rem 1rem' }}>
                <i className="fa fa-exclamation-triangle me-2"></i>
                Embed data is incomplete. Missing embedUrl or embedToken.
              </div>
            )}

            {!embedLoading && !embedError && !embedData && (
              <div style={{
                background: '#f8f9fa',
                padding: '30px',
                borderRadius: '10px',
                border: '1px solid #e0e0e0',
                marginTop: '80px',
                margin: '80px 1rem 1rem 1rem'
              }}>
                <h4 style={{ color: '#667eea', marginBottom: '15px' }}>Report Details</h4>
                <div style={{ fontSize: '1rem', color: '#666' }}>
                  <p><strong>Report ID:</strong> {selectedReport.id}</p>
                  {selectedReport.report_id && <p><strong>Power BI ID:</strong> {selectedReport.report_id}</p>}
                  <p style={{ marginTop: '20px', fontStyle: 'italic', color: '#999' }}>
                    Loading embed data...
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zoom Controls - Positioned at root level to avoid z-index issues */}
      {!isProfileView && selectedReport && !embedLoading && !embedError && embedData && (
        <ZoomControls
          zoomLevel={zoomLevel}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFitToScreen={handleFitToScreen}
          onZoomChange={handleZoomChange}
        />
      )}
    </div>
  );
};

export default PowerBIPortal;
